import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { fmtEUR } from '@/lib/utils';
import { getWatchlist, removeFromWatchlist } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Heart, Trash2, Package, Calendar } from 'lucide-react';
import { LazyImage } from '@/components/ui/LazyImage';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1683765084506-3ad53915aa01?w=400&h=300&fit=crop';

export const WatchlistPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) loadWatchlist();
  }, [user]);

  const loadWatchlist = async () => {
    try {
      const response = await getWatchlist();
      setItems(response.data);
    } catch (error) {
      console.error('Failed to load watchlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (materialId) => {
    try {
      await removeFromWatchlist(materialId);
      setItems(prev => prev.filter(item => item.material_id !== materialId));
      toast.success(t('material.removeFromWatchlist'));
    } catch (error) {
      toast.error(t('watchlist.remove'));
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center" data-testid="watchlist-login-required">
          <Heart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('watchlist.loginRequired')}</h1>
          <p className="text-muted-foreground mb-6">{t('watchlist.loginHint')}</p>
          <Button asChild className="bg-orange-500 hover:bg-orange-600">
            <Link to="/login">{t('nav.login')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="watchlist-page">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-manrope text-3xl font-bold" data-testid="watchlist-title">{t('nav.watchlist')}</h1>
            {items.length > 0 && (
              <p className="text-muted-foreground mt-1" data-testid="watchlist-count">
                {t('watchlist.itemCount', { count: items.length })}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => (
              <Card key={item.material_id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`watchlist-item-${item.material_id}`}>
                <div className="flex">
                  <Link to={`/material/${item.material_id}`} className="w-32 shrink-0">
                    <div className="h-full bg-slate-100 dark:bg-slate-800">
                      <LazyImage
                        src={item.photo?.thumbnail_url || item.photo?.file_url || PLACEHOLDER_IMAGE}
                        blurSrc={item.photo?.blur_hash}
                        alt={item.material?.common_title}
                        className="w-full h-full"
                        fallbackSrc={PLACEHOLDER_IMAGE}
                      />
                    </div>
                  </Link>
                  
                  <CardContent className="flex-1 p-4">
                    <Link to={`/material/${item.material_id}`}>
                      <h3 className="font-semibold line-clamp-2 hover:text-orange-500 transition-colors" data-testid={`watchlist-item-title-${item.material_id}`}>
                        {item.material?.common_title}
                      </h3>
                    </Link>
                    
                    <p className="text-sm text-muted-foreground mt-1">
                      {item.material?.manufacturer_name}
                    </p>
                    
                    {item.skus?.[0] && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <Package className="h-3 w-3" />
                          {item.skus[0].quantity} {item.skus[0].uom}
                        </div>
                        <p className="text-lg font-bold text-orange-500">
                          {item.skus[0].book_value_kEUR 
                            ? `\u20AC${fmtEUR(item.skus[0].book_value_kEUR)}`
                            : t('watchlist.priceOnRequest')
                          }
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {t('watchlist.addedOn')} {new Date(item.added_at).toLocaleDateString()}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2"
                        onClick={() => handleRemove(item.material_id)}
                        data-testid={`watchlist-remove-${item.material_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t('watchlist.remove')}
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="watchlist-empty">
            <Heart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('watchlist.empty')}</h2>
            <p className="text-muted-foreground mb-6">{t('watchlist.emptyHint')}</p>
            <Button asChild className="bg-orange-500 hover:bg-orange-600">
              <Link to="/" data-testid="watchlist-browse-btn">{t('watchlist.browse')}</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
